// src/components/PremiumChat.js — fixed image upload error handling
import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../hooks/useAuth'
import { supabase, sendMessage, SKY_BRI_USER_ID, markConversationRead } from '../lib/supabase'
import { compressImage, checkImageLimit, incrementImageUsage, getImageSettings } from '../lib/imageUtils'
import { makePending, resolvePending, failPending, markSending, keepUnsent, applyReadReceipt } from '../lib/chatUtils'
import useChatScroll from '../hooks/useChatScroll'
import useTyping from '../hooks/useTyping'
import ChatThread, { JumpToLatest, TypingIndicator } from './ChatThread'
import ChatPhotoViewer from './ChatPhotoViewer'
import './Chat.css'

export default function PremiumChat({ tier, onUnreadChange }) {
  const { session }             = useAuth()
  const userId                  = session?.user?.id
  const [messages, setMessages] = useState([])
  const [input, setInput]       = useState('')
  const [viewerSrc, setViewerSrc] = useState(null)
  const [imgEnabled, setImgEnabled]   = useState(true)
  const [imgUploading, setImgUploading] = useState(false)
  const [imgError, setImgError]       = useState(null)
  const [unreadCount, setUnreadCount] = useState(0)
  const fileRef   = useRef(null)
  const tabActive = useRef(true)
  const { otherTyping, notifyTyping, clearTyping } = useTyping(userId, userId)
  const { containerRef, bottomRef, onScroll, showJump, jumpToLatest } =
    useChatScroll(messages, userId, userId, otherTyping)

  useEffect(() => {
    if (!userId) return
    loadMessages()
    loadImageSettings()

    const sub = supabase.channel('premium-chat-' + userId)
      .on('postgres_changes', {
        event: 'INSERT', schema: 'public', table: 'messages',
        filter: `receiver_id=eq.${userId}`
      }, payload => {
        setMessages(prev => [...prev, payload.new])
        clearTyping()
        // They're looking at the chat right now, so this counts as read
        if (!document.hidden) markConversationRead(SKY_BRI_USER_ID)
        if (!tabActive.current) {
          setUnreadCount(c => {
            const n = c + 1
            if (onUnreadChange) onUnreadChange(n)
            return n
          })
        }
      })
      // Sky Bri read one of my messages -> show "Seen" instantly
      .on('postgres_changes', {
        event: 'UPDATE', schema: 'public', table: 'messages',
        filter: `sender_id=eq.${userId}`
      }, payload => setMessages(prev => applyReadReceipt(prev, payload.new)))
      .subscribe()

    const handleVis = () => {
      tabActive.current = !document.hidden
      if (!document.hidden) markConversationRead(SKY_BRI_USER_ID)
    }
    document.addEventListener('visibilitychange', handleVis)

    return () => {
      sub.unsubscribe()
      document.removeEventListener('visibilitychange', handleVis)
    }
  }, [userId]) // eslint-disable-line

  async function loadMessages() {
    const { data } = await supabase
      .from('messages').select('*')
      .or(`sender_id.eq.${userId},receiver_id.eq.${userId}`)
      .order('created_at', { ascending: true })
    if (data) {
      setMessages(prev => keepUnsent(data, prev))
      if (!document.hidden && data.some(m => m.sender_id !== userId && m.read_at === null)) {
        markConversationRead(SKY_BRI_USER_ID)
      }
    }
  }

  async function loadImageSettings() {
    const s = await getImageSettings(supabase)
    setImgEnabled(s.enabled)
  }

  function handleFocus() {
    setUnreadCount(0)
    if (onUnreadChange) onUnreadChange(0)
    tabActive.current = true
  }

  // Send to the server and settle the on-screen bubble either way
  async function deliver(tempId, content) {
    const { data: msg, error } = await sendMessage({ senderId: userId, receiverId: SKY_BRI_USER_ID, content })
    if (error || !msg) setMessages(prev => failPending(prev, tempId))
    else setMessages(prev => resolvePending(prev, tempId, msg))
  }

  function handleSend(e) {
    e?.preventDefault()
    const content = input.trim()
    if (!content) return
    setInput('')
    // Show it instantly; a failure becomes a visible "Not sent · Retry" instead of vanishing
    const pending = makePending({ senderId: userId, receiverId: SKY_BRI_USER_ID, content })
    setMessages(prev => [...prev, pending])
    deliver(pending.id, content)
  }

  function retryMessage(id) {
    const m = messages.find(x => x.id === id)
    if (!m) return
    setMessages(prev => markSending(prev, id))
    deliver(id, m.content)
  }
  function discardMessage(id) { setMessages(prev => prev.filter(x => x.id !== id)) }

  async function handleImageSelect(e) {
    const file = e.target.files[0]
    if (!file) return
    e.target.value = ''
    setImgError(null)

    if (!imgEnabled) { setImgError('Image sharing is currently unavailable.'); return }
    if (!file.type.startsWith('image/')) { setImgError('Only image files can be sent.'); return }

    // Check daily limit silently
    const { canSend } = await checkImageLimit(supabase, userId, tier)
    if (!canSend) {
      setImgError('You have reached your image limit for today. Try again tomorrow.')
      return
    }

    setImgUploading(true)
    try {
      // Compress image
      const compressed = await compressImage(file, 500)

      // Upload to storage
      const filename = `chat/${userId}/${Date.now()}.jpg`
      const { error: upErr } = await supabase.storage
        .from('chat-images')
        .upload(filename, compressed, { contentType: 'image/jpeg', cacheControl: '3600' })

      if (upErr) {
        console.error('Upload error:', upErr)
        setImgError('Upload failed. Check your connection and try again.')
        return
      }

      const { data: { publicUrl } } = supabase.storage.from('chat-images').getPublicUrl(filename)

      // Send message with image
      const { data: msg, error: msgErr } = await sendMessage({
        senderId: userId,
        receiverId: SKY_BRI_USER_ID,
        content: `[IMAGE]${publicUrl}`
      })

      if (msgErr) {
        setImgError('Image uploaded but failed to send. Try again.')
        return
      }

      if (msg) {
        setMessages(prev => [...prev, msg])
        await incrementImageUsage(supabase, userId)
      }
    } catch (err) {
      console.error('Image error:', err)
      setImgError('Something went wrong. Please try again.')
    } finally {
      setImgUploading(false)
    }
  }

  return (
    <div className="chat-container" onClick={handleFocus}>
      {unreadCount > 0 && (
        <div className="chat-unread-banner">
          {unreadCount} new message{unreadCount > 1 ? 's' : ''} from Sky Bri
        </div>
      )}

      <div className="chat-messages" ref={containerRef} onScroll={onScroll}>
        {messages.length === 0 && (
          <div className="chat-empty">
            <p className="chat-empty__title">Your private line to Sky Bri</p>
            <p className="chat-empty__sub">Send your first message</p>
          </div>
        )}
        <ChatThread
          messages={messages}
          userId={userId}
          showSkyAvatar
          onRetry={retryMessage}
          onDiscard={discardMessage}
          onOpenImage={setViewerSrc}
        />
        <TypingIndicator show={otherTyping} name="Sky Bri" />
        <JumpToLatest show={showJump} onClick={jumpToLatest} />
        <div ref={bottomRef} />
      </div>

      {/* Inline image error — no popup */}
      {imgError && (
        <div className="chat-img-error">
          {imgError}
          <button onClick={() => setImgError(null)}>×</button>
        </div>
      )}

      <form className="chat-input-row" onSubmit={handleSend}>
        {imgEnabled && (
          <>
            <button type="button" className="chat-img-btn"
              onClick={() => fileRef.current?.click()} disabled={imgUploading}
              title="Send image">
              {imgUploading ? '⏳' : '📷'}
            </button>
            <input ref={fileRef} type="file" accept="image/*"
              onChange={handleImageSelect} style={{ display: 'none' }} />
          </>
        )}
        <input className="chat-input" placeholder="Message Sky Bri…"
          value={input} onChange={e => { setInput(e.target.value); if (e.target.value) notifyTyping() }}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() } }}
          autoComplete="off" />
        <button className="chat-send" type="submit" disabled={!input.trim()}>
          ↑
        </button>
      </form>

      <ChatPhotoViewer src={viewerSrc} onClose={() => setViewerSrc(null)} />
    </div>
  )
}
